/**
 * Слэш-команды в поле ввода (ADR 0010) — чистая логика без React и сторов: список команд
 * с подкомандами для подсказки «/», разбор того, что набрано в полях опций, и опции для тела
 * POST /interactions. Проверки — scripts/commands.test.mjs.
 */
import { CommandType, OptionType, type ApplicationCommand, type ApplicationCommandIndex, type CommandApplication, type CommandChoice, type CommandOption, type InteractionDataOption } from '@/types/api'
import { matches } from './autocomplete'

/** Строка подсказки «/»: команда или её подкоманда. */
export interface CommandEntry {
  /** Уникален в списке: id команды и путь подкоманды. */
  key: string
  command: ApplicationCommand
  app: CommandApplication | undefined
  /** [] — сама команда, [подкоманда] или [группа, подкоманда]. */
  path: string[]
  /** Полное имя без «/»: «музыка играй». */
  name: string
  description: string
  /** Опции того, что вызывают: подкоманды или команды без подкоманд. */
  options: CommandOption[]
}

const collator = new Intl.Collator('ru')

const isNested = (o: CommandOption) => o.type === OptionType.SubCommand || o.type === OptionType.SubCommandGroup

/**
 * Команды из списка сервера — плоско, как их выбирают: у команды с подкомандами вызывают только
 * подкоманду, поэтому строкой становится каждая («/музыка играй»), а сама команда — нет.
 * Меню по правому клику (USER, MESSAGE) пропускаем: их клиент пока не показывает.
 * Порядок: по приложению, внутри — по имени.
 */
export function flattenCommands(index: ApplicationCommandIndex | null | undefined): CommandEntry[] {
  if (!index) return []
  const apps = new Map((index.applications ?? []).map((a) => [a.id, a]))
  const out: CommandEntry[] = []
  for (const command of index.application_commands ?? []) {
    if ((command.type ?? CommandType.ChatInput) !== CommandType.ChatInput) continue
    const app = apps.get(command.application_id)
    const entry = (path: string[], description: string, options: CommandOption[] | undefined): CommandEntry => ({
      key: [command.id, ...path].join(' '),
      command,
      app,
      path,
      name: [command.name, ...path].join(' '),
      description,
      options: (options ?? []).filter((o) => !isNested(o)),
    })
    const nested = (command.options ?? []).filter(isNested)
    if (!nested.length) {
      out.push(entry([], command.description, command.options))
      continue
    }
    for (const o of nested) {
      if (o.type === OptionType.SubCommand) out.push(entry([o.name], o.description, o.options))
      else for (const sub of o.options ?? []) if (sub.type === OptionType.SubCommand) out.push(entry([o.name, sub.name], sub.description, sub.options))
    }
  }
  const appName = (e: CommandEntry) => e.app?.name ?? ''
  return out.sort(
    (a, b) =>
      collator.compare(appName(a), appName(b)) ||
      a.command.application_id.localeCompare(b.command.application_id) ||
      collator.compare(a.name, b.name),
  )
}

/** Что набрано после «/» в начале поля; null — это не набор команды (нет «/» в начале или уже вторая строка). */
export function commandQuery(draft: string): string | null {
  const m = /^\/([^\n]{0,100})$/.exec(draft)
  return m ? (m[1] ?? '').toLowerCase() : null
}

/** Команды под набранное: имя или любое его слово начинается с запроса («иг» найдёт «/музыка играй»). */
export function filterCommands(entries: readonly CommandEntry[], query: string): CommandEntry[] {
  const q = query.toLowerCase()
  if (!q) return [...entries]
  return entries.filter((e) => e.name.startsWith(q) || e.name.split(' ').some((w) => w.startsWith(q)))
}

/**
 * Какую строку выделить сразу: первую, чьё полное имя начинается с набранного. Строки идут по
 * приложениям, и «/по» иначе выделило бы «/музыка очередь показать», а не «/погода».
 */
export function bestCommand(items: readonly CommandEntry[], query: string): number {
  const q = query.toLowerCase()
  const i = q ? items.findIndex((e) => e.name.startsWith(q)) : 0
  return Math.max(i, 0)
}

/**
 * Имя команды набрали целиком и поставили пробел — выбираем её сама, как Discord. Кроме случая,
 * когда это группа: «/музыка » ещё не команда, дальше выбирают подкоманду.
 */
export function typedCommand(entries: readonly CommandEntry[], query: string): CommandEntry | undefined {
  if (!query.endsWith(' ')) return undefined
  const hit = entries.find((e) => e.name === query.trimEnd())
  return hit && !entries.some((e) => e.name.startsWith(query)) ? hit : undefined
}

// --- поля опций ---------------------------------------------------------------------------

/** Выбранное из списка: значение для сервера и что видно в поле. */
export interface Picked {
  value: string | number | boolean
  label: string
}

/** Файл опции-вложения: File в браузере, простой объект в тестах. */
export interface FileLike {
  name: string
  size: number
  type?: string
}

export interface FieldState {
  /** Что видно в поле ввода. */
  text: string
  picked?: Picked | undefined
  file?: FileLike | undefined
}

export type FieldValues = Record<string, FieldState>

export type Checked = { state: 'empty' } | { state: 'ok'; value: string | number | boolean } | { state: 'error'; error: string }

/** Какой список предлагать в поле: участники, роли, те и другие, каналы. */
export type PickKind = 'user' | 'role' | 'mentionable' | 'channel'

export function pickKind(opt: Pick<CommandOption, 'type'>): PickKind | null {
  switch (opt.type) {
    case OptionType.User:
      return 'user'
    case OptionType.Role:
      return 'role'
    case OptionType.Mentionable:
      return 'mentionable'
    case OptionType.Channel:
      return 'channel'
    default:
      return null
  }
}

/** Значение только из choices: у опции есть варианты и нет автодополнения. */
export const fixedChoices = (opt: Pick<CommandOption, 'choices' | 'autocomplete'>) => Boolean(opt.choices?.length) && !opt.autocomplete

/** Поле, где значение выбирают из списка, а не пишут: без выбора его не отправить. */
export const picksOnly = (opt: CommandOption) => pickKind(opt) !== null || fixedChoices(opt) || opt.type === OptionType.Boolean

export function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return one
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few
  return many
}

const formatNumber = (n: number) => n.toLocaleString('ru-RU')

const MINUS = String.fromCharCode(0x2212)

/**
 * Число из поля: пробелы между разрядами убираем, запятую считаем точкой — по-русски дробь
 * пишут «2,5». Целое — только цифры; больше 2^53 Discord не принимает, и мы тоже.
 */
export function parseNumber(text: string, integer: boolean): number | null {
  // \s в JS ловит и неразрывные пробелы; типографский минус (U+2212) считаем обычным
  const s = text.trim().replace(/\s+/g, '').split(MINUS).join('-').replace(',', '.')
  const ok = integer ? /^[-+]?\d+$/.test(s) : /^[-+]?(\d+\.?\d*|\.\d+)(e[-+]?\d+)?$/i.test(s)
  if (!ok) return null
  const n = Number(s)
  if (!Number.isFinite(n) || (integer && !Number.isSafeInteger(n))) return null
  return n
}

const YES = new Set(['да', 'true', 'yes'])
const NO = new Set(['нет', 'false', 'no'])

export function parseBoolean(text: string): boolean | null {
  const t = text.trim().toLowerCase()
  return YES.has(t) ? true : NO.has(t) ? false : null
}

/** Вариант по тому, что набрали: по имени без учёта регистра или по самому значению. */
export function findChoice(choices: readonly CommandChoice[], text: string): CommandChoice | undefined {
  const t = text.trim().toLowerCase()
  return choices.find((c) => c.name.toLowerCase() === t) ?? choices.find((c) => String(c.value).toLowerCase() === t)
}

const PICK_ERRORS: Record<PickKind, string> = {
  user: 'Выберите участника из списка',
  role: 'Выберите роль из списка',
  mentionable: 'Выберите участника или роль из списка',
  channel: 'Выберите канал из списка',
}

/** Разбор одного поля: пусто, готовое значение или что не так. */
export function checkOption(opt: CommandOption, field: FieldState | undefined): Checked {
  if (opt.type === OptionType.Attachment) return field?.file ? { state: 'ok', value: '' } : { state: 'empty' }
  if (field?.picked) return { state: 'ok', value: field.picked.value }
  const text = field?.text.trim() ?? ''
  if (!text) return { state: 'empty' }
  const error = (e: string): Checked => ({ state: 'error', error: e })
  const kind = pickKind(opt)
  if (kind) return error(PICK_ERRORS[kind])
  if (opt.type === OptionType.Boolean) {
    const b = parseBoolean(text)
    return b === null ? error('Выберите «Да» или «Нет»') : { state: 'ok', value: b }
  }
  if (fixedChoices(opt)) {
    const c = findChoice(opt.choices ?? [], text)
    return c ? { state: 'ok', value: c.value } : error('Выберите вариант из списка')
  }
  if (opt.type === OptionType.Integer || opt.type === OptionType.Number) {
    const n = parseNumber(text, opt.type === OptionType.Integer)
    if (n === null) return error(opt.type === OptionType.Integer ? 'Нужно целое число' : 'Нужно число')
    if (opt.min_value !== undefined && n < opt.min_value) return error(`Не меньше ${formatNumber(opt.min_value)}`)
    if (opt.max_value !== undefined && n > opt.max_value) return error(`Не больше ${formatNumber(opt.max_value)}`)
    return { state: 'ok', value: n }
  }
  // длину считаем символами, а не половинками UTF-16: эмодзи — один символ
  const length = [...text].length
  if (opt.min_length !== undefined && length < opt.min_length) return error(`Не короче ${opt.min_length} ${plural(opt.min_length, 'символа', 'символов', 'символов')}`)
  if (opt.max_length !== undefined && length > opt.max_length) return error(`Не длиннее ${opt.max_length} ${plural(opt.max_length, 'символа', 'символов', 'символов')}`)
  return { state: 'ok', value: text }
}

/** Опции, которые стоят в поле сразу: обязательные, в порядке команды. */
export function requiredOptions(entry: Pick<CommandEntry, 'options'>): string[] {
  return entry.options.filter((o) => o.required).map((o) => o.name)
}

/** Опции листа — в обёртку подкоманды и группы, как их ждёт бот. */
export function wrapOptions(path: readonly string[], leaf: InteractionDataOption[]): InteractionDataOption[] {
  const [first, second] = path
  if (first === undefined) return leaf
  if (second === undefined) return [{ name: first, type: OptionType.SubCommand, options: leaf }]
  return [{ name: first, type: OptionType.SubCommandGroup, options: [{ name: second, type: OptionType.SubCommand, options: leaf }] }]
}

export interface BuiltOptions {
  /** data.options запроса, уже с подкомандой и группой. */
  options: InteractionDataOption[]
  /** Что не так: имя опции → текст. Пусто — можно отправлять. */
  errors: Record<string, string>
  /** Опции-вложения по порядку: i-я получает value String(i) и id i в data.attachments. */
  files: string[]
}

/**
 * data.options для выполнения команды. shown — какие необязательные опции добавлены в поле;
 * обязательные входят всегда. Пустое необязательное поле просто не отправляется.
 */
export function buildCommandOptions(entry: Pick<CommandEntry, 'options' | 'path'>, values: FieldValues, shown: readonly string[]): BuiltOptions {
  const leaf: InteractionDataOption[] = []
  const errors: Record<string, string> = {}
  const files: string[] = []
  for (const opt of entry.options) {
    if (!opt.required && !shown.includes(opt.name)) continue
    const c = checkOption(opt, values[opt.name])
    if (c.state === 'error') errors[opt.name] = c.error
    else if (c.state === 'empty') {
      if (opt.required) errors[opt.name] = opt.type === OptionType.Attachment ? 'Выберите файл' : 'Заполните поле'
    } else if (opt.type === OptionType.Attachment) {
      leaf.push({ name: opt.name, type: opt.type, value: String(files.length) })
      files.push(opt.name)
    } else leaf.push({ name: opt.name, type: opt.type, value: c.value })
  }
  return { options: wrapOptions(entry.path, leaf), errors, files }
}

/**
 * data.options для автодополнения (type 4): набираемое поле — строкой как есть и с focused,
 * остальные — только уже годные значения. Файлы ради подсказки не загружаем.
 */
export function buildAutocompleteOptions(entry: Pick<CommandEntry, 'options' | 'path'>, values: FieldValues, shown: readonly string[], focused: string): InteractionDataOption[] {
  const leaf: InteractionDataOption[] = []
  for (const opt of entry.options) {
    if (opt.name === focused) {
      leaf.push({ name: opt.name, type: opt.type, value: values[opt.name]?.text ?? '', focused: true })
      continue
    }
    if ((!opt.required && !shown.includes(opt.name)) || opt.type === OptionType.Attachment) continue
    const c = checkOption(opt, values[opt.name])
    if (c.state === 'ok') leaf.push({ name: opt.name, type: opt.type, value: c.value })
  }
  return wrapOptions(entry.path, leaf)
}

// --- кандидаты для полей и списков --------------------------------------------------------

export interface PickUser {
  id: string
  /** Как показываем: ник на сервере или отображаемое имя. */
  name: string
  username: string
  bot?: boolean | undefined
  avatar?: string | null | undefined
}

export interface PickRole {
  id: string
  name: string
  color?: string | undefined
}

export interface PickChannel {
  id: string
  name: string
  type: number
}

/** Кого и что можно выбрать в поле или списке: участники, роли и каналы этого чата. */
export interface PickEnv {
  users: PickUser[]
  roles: PickRole[]
  channels: PickChannel[]
}

export interface Candidate {
  key: string
  kind: 'user' | 'role' | 'channel' | 'choice'
  label: string
  hint?: string | undefined
  color?: string | undefined
  bot?: boolean | undefined
  avatar?: string | null | undefined
  channelType?: number | undefined
  value: string | number | boolean
}

/**
 * Участники, роли и каналы под набранное. Для «участника или роли» половина мест — ролям,
 * иначе на большом сервере до них не дойти.
 */
export function pickCandidates(env: PickEnv, kind: PickKind, query: string, channelTypes?: readonly number[] | undefined, limit = 25): Candidate[] {
  const q = query.trim().replace(/^[@#]/, '')
  // сначала те, чьё имя начинается с набранного: «ва» — это Вася, а не Аня Петрова
  const starts = (...names: string[]) => (q && names.some((n) => n.toLowerCase().startsWith(q.toLowerCase())) ? 0 : 1)
  if (kind === 'channel') {
    return env.channels
      .filter((c) => (!channelTypes?.length || channelTypes.includes(c.type)) && matches(q, c.name))
      .sort((a, b) => starts(a.name) - starts(b.name) || collator.compare(a.name, b.name))
      .slice(0, limit)
      .map((c) => ({ key: `c:${c.id}`, kind: 'channel', label: c.name, channelType: c.type, value: c.id }))
  }
  const users: Candidate[] =
    kind === 'role'
      ? []
      : env.users
          .filter((u) => matches(q, u.name, u.username))
          .sort((a, b) => starts(a.name, a.username) - starts(b.name, b.username) || collator.compare(a.name, b.name))
          .map((u) => ({ key: `u:${u.id}`, kind: 'user', label: u.name, hint: `@${u.username}`, bot: u.bot, avatar: u.avatar, value: u.id }))
  const roles: Candidate[] =
    kind === 'user' ? [] : env.roles.filter((r) => matches(q, r.name)).map((r) => ({ key: `r:${r.id}`, kind: 'role', label: r.name, hint: 'роль', color: r.color, value: r.id }))
  const r = kind === 'mentionable' ? roles.slice(0, Math.floor(limit / 2)) : roles.slice(0, limit)
  return [...users.slice(0, limit - r.length), ...r]
}

/** Варианты опции под набранное (choices команды или ответ автодополнения). */
export function choiceCandidates(choices: readonly CommandChoice[], query: string): Candidate[] {
  return choices
    .filter((c) => matches(query.trim(), c.name))
    .map((c, i) => ({ key: `v:${i}:${String(c.value)}`, kind: 'choice', label: c.name, value: c.value }))
}

export function booleanCandidates(query: string): Candidate[] {
  return [
    { key: 'b:1', kind: 'choice' as const, label: 'Да', value: true },
    { key: 'b:0', kind: 'choice' as const, label: 'Нет', value: false },
  ].filter((c) => matches(query.trim(), c.label))
}

/** Что встаёт в поле после выбора: «@Ник», «#канал» или имя варианта. */
export function pickedLabel(c: Pick<Candidate, 'kind' | 'label'>): string {
  if (c.kind === 'channel') return `#${c.label}`
  if (c.kind === 'user' || c.kind === 'role') return c.label.startsWith('@') ? c.label : `@${c.label}`
  return c.label
}

/**
 * Набрали имя целиком и не выбрали из списка — берём это имя, если оно одно такое.
 * Сравниваем с тем, что видно в поле после выбора, с именем и с @логином.
 */
export function exactCandidate(cands: readonly Candidate[], text: string): Candidate | undefined {
  const t = text.trim().toLowerCase()
  if (!t) return undefined
  const login = (c: Candidate) => c.kind === 'user' && (c.hint?.toLowerCase() === t || c.hint?.toLowerCase() === `@${t}`)
  const hits = cands.filter((c) => pickedLabel(c).toLowerCase() === t || c.label.toLowerCase() === t || login(c))
  return hits.length === 1 ? hits[0] : undefined
}
